import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiShield, FiLock, FiUnlock, FiMail, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { AdminSidebar, ResetPasswordModal } from '../../components/admin';
import api from '../../services/api'; 
import { Modal, Button, Badge, Table, Alert, Form } from 'react-bootstrap'; 

const AdminAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', msg: '' });

  const [showFormModal, setShowFormModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); 
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'ADMIN' });

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmLock, setShowConfirmLock] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [adminToProcess, setAdminToProcess] = useState(null);

  // ✅ RÉCUPÉRATION DES DONNÉES
  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins');
      // On log pour vérifier le nom exact du champ (accountLocked ?)
      console.log("Liste des admins reçue:", res.data.content || res.data);
      setAdmins(res.data.content || res.data);
    } catch (err) {
      setStatus({ type: 'danger', msg: 'Erreur d\'accès aux données.' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // --- ACTIONS ---

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') {
        await api.post('/admins', form);
        setStatus({ type: 'success', msg: `Invitation envoyée.` });
      } else {
        await api.put(`/admins/${selectedAdmin.id}`, form);
        setStatus({ type: 'success', msg: 'Modifications enregistrées.' });
      }
      setShowFormModal(false);
      fetchAdmins();
    } catch (err) { setStatus({ type: 'danger', msg: 'Erreur.' }); }
  };

  // ✅ EXÉCUTER LE BLOCAGE / DÉBLOCAGE
  const executeToggleLock = async () => {
    try {
      await api.put(`/admins/${adminToProcess.id}/toggle-lock`);
      
      // On ferme la modale et on recharge les données
      setShowConfirmLock(false);
      
      // Petit délai pour laisser le temps à la BD de répondre
      setTimeout(() => {
          fetchAdmins();
          setStatus({ type: 'success', msg: "Le statut du compte a été mis à jour avec succès." });
      }, 300);

    } catch (err) {
      setStatus({ type: 'danger', msg: "Erreur serveur lors du changement de statut." });
      setShowConfirmLock(false);
    }
  };

  const executeDelete = async () => {
    try {
      await api.delete(`/admins/${adminToProcess.id}`);
      setShowConfirmDelete(false);
      fetchAdmins();
      setStatus({ type: 'success', msg: "Administrateur supprimé." });
    } catch (err) {
      setStatus({ type: 'danger', msg: "Erreur lors de la suppression." });
      setShowConfirmDelete(false);
    }
  };

  return (
    <div className="admin-admins-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gestion des Accès</h2>
          <Button variant="primary" onClick={() => { setModalMode('add'); setShowFormModal(true); }}>
            <FiPlus /> Ajouter un admin
          </Button>
        </div>

        {status.msg && <Alert variant={status.type} dismissible onClose={() => setStatus({})}>{status.msg}</Alert>}

        <Table responsive hover className="admin-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => {
                // 🚩 ATTENTION : On vérifie les deux noms possibles pour être sûr
                const isLocked = admin.accountLocked || admin.account_locked;

                return (
                  <tr key={admin.id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>
                      {isLocked ? (
                        <Badge bg="secondary"><FiLock /> BLOQUÉ</Badge>
                      ) : (
                        <Badge bg="success"><FiUnlock /> ACTIF</Badge>
                      )}
                    </td>
                    <td>
                      <Button variant="link" 
                              className={isLocked ? "text-success" : "text-warning"} 
                              onClick={() => { setAdminToProcess(admin); setShowConfirmLock(true); }}>
                        {isLocked ? <FiUnlock /> : <FiLock />}
                      </Button>
                      <Button variant="link" className="text-primary" 
                              onClick={() => { setAdminToProcess(admin); setShowResetModal(true); }}>
                        <FiRefreshCw />
                      </Button>
                      <Button variant="link" className="text-danger" 
                              onClick={() => { setAdminToProcess(admin); setShowConfirmDelete(true); }}>
                        <FiTrash2 />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
        </Table>
      </div>

      {/* 🔒 MODALE DE BLOCAGE DYNAMIQUE */}
      <Modal show={showConfirmLock} onHide={() => setShowConfirmLock(false)} centered>
        <Modal.Body className="text-center p-4">
          {/* On vérifie à nouveau les deux noms de champs pour l'icône */}
          {(adminToProcess?.accountLocked || adminToProcess?.account_locked) ? (
             <div className="text-success mb-3"><FiUnlock size={60} /></div>
          ) : (
             <div className="text-warning mb-3"><FiLock size={60} /></div>
          )}

          <h4 className="fw-bold">
            {(adminToProcess?.accountLocked || adminToProcess?.account_locked) ? 'Rétablir l\'accès ?' : 'Suspendre l\'accès ?'}
          </h4>
          
          <p className="text-muted">
            Voulez-vous modifier les droits pour <strong>{adminToProcess?.name}</strong> ?
          </p>

          <div className="d-flex gap-3 justify-content-center mt-4">
            <Button variant="light" onClick={() => setShowConfirmLock(false)}>Annuler</Button>
            <Button 
              variant={(adminToProcess?.accountLocked || adminToProcess?.account_locked) ? "success" : "warning"} 
              onClick={executeToggleLock}
            >
              Confirmer
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DE SUPPRESSION (Identique) */}
      <Modal show={showConfirmDelete} onHide={() => setShowConfirmDelete(false)} centered>
        <Modal.Body className="text-center p-4">
          <FiAlertTriangle size={60} className="text-danger mb-3" />
          <h4 className="fw-bold">Suppression Définitive</h4>
          <p className="text-muted">Supprimer <strong>{adminToProcess?.name}</strong> de la base de données ?</p>
          <div className="d-flex gap-3 justify-content-center mt-4">
            <Button variant="light" onClick={() => setShowConfirmDelete(false)}>Annuler</Button>
            <Button variant="danger" onClick={executeDelete}>Supprimer</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE FORMULAIRE (Identique) */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Nouvel admin</Modal.Title></Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom</Form.Label>
              <Form.Control value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="submit" className="w-100">Lancer l'invitation</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* 🔄 MODALE DE RÉINITIALISATION MOT DE PASSE */}
      <ResetPasswordModal 
        show={showResetModal} 
        onHide={() => setShowResetModal(false)} 
        admin={adminToProcess} 
        onSuccess={(msg) => setStatus({ type: 'success', msg })}
      />
    </div>
  );
};

export default AdminAdmins;